/**
 * Réduit la taille des photos d'identité / cartes avant upload (multipart).
 * Les PDF sont renvoyés tels quels. Sortie JPEG pour les images matricielles.
 */

const OUTPUT_TYPE = 'image/jpeg';
/** Cible indicative par fichier (~plusieurs fichiers dans la même requête HTTP) */
const MAX_TARGET_BYTES = 750000;

function isCompressibleImage(file) {
  if (!file || !file.type) return false;
  return /^image\/(jpeg|jpg|png|webp|heic|heif)$/i.test(file.type);
}

function baseName(file) {
  const n = file.name || 'image';
  return n.replace(/\.[^./\\]+$/, '') || 'image';
}

function toBlob(canvas, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), OUTPUT_TYPE, quality);
  });
}

/**
 * @param {File} file
 * @returns {Promise<File>}
 */
export async function compressImageForUpload(file) {
  if (!isCompressibleImage(file)) {
    return file;
  }

  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }

  const srcW = bitmap.width;
  const srcH = bitmap.height;
  if (!srcW || !srcH) {
    bitmap.close();
    return file;
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const maxDims = [2200, 1920, 1600, 1280, 1024, 900, 800];
  const qualities = [0.88, 0.8, 0.72, 0.64, 0.56, 0.5];

  let bestSoFar = null;
  let underCap = null;

  try {
    outer: for (const maxDim of maxDims) {
      const scale = Math.min(1, maxDim / Math.max(srcW, srcH));
      const w = Math.max(1, Math.round(srcW * scale));
      const h = Math.max(1, Math.round(srcH * scale));

      canvas.width = w;
      canvas.height = h;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(bitmap, 0, 0, w, h);

      for (const q of qualities) {
        const blob = await toBlob(canvas, q);
        if (!blob) continue;
        if (!bestSoFar || blob.size < bestSoFar.size) {
          bestSoFar = blob;
        }
        if (blob.size <= MAX_TARGET_BYTES) {
          underCap = blob;
          break outer;
        }
      }
    }
  } finally {
    try {
      bitmap.close();
    } catch {
      /* ignore */
    }
  }

  const chosen = underCap || bestSoFar;
  if (!chosen || chosen.size >= file.size) {
    return file;
  }

  return new File([chosen], `${baseName(file)}.jpg`, {
    type: OUTPUT_TYPE,
    lastModified: Date.now(),
  });
}
